import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import HomePage from './components/HomePage';
import LoginPage from './components/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import SignupPage from './pages/SignupPage';
import JobsPage from './pages/JobsPage';
import NewBakePage from './pages/NewBakePage';
import JobDetailPage from './pages/JobDetailPage';
import PartnerRiggingJobsPage from './pages/PartnerRiggingJobsPage';
import ProjectsListPage from './pages/ProjectsListPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectFilesPage from './pages/ProjectFilesPage';
import ProjectSettingsPage from './pages/ProjectSettingsPage';
import ProductionDepartmentPage from './pages/ProductionDepartmentPage';
import ProductionWorkflowPage from './pages/ProductionWorkflowPage';
import ProjectRiggingPage from './pages/ProjectRiggingPage';
import TechnicalArtistPage from './pages/TechnicalArtistPage';
import ConsoleLayout from './console/ConsoleLayout';
import PlatformLayout from './console/PlatformLayout';
import RequireTenantPermission from './console/RequireTenantPermission';
import RequirePlatformPermission from './console/RequirePlatformPermission';
import OverviewPage from './pages/console/OverviewPage';
import TeamPage from './pages/console/TeamPage';
import TeamRolesPage from './pages/console/TeamRolesPage';
import CreateTenantPage from './pages/console/CreateTenantPage';
import ApiKeysPage from './pages/console/ApiKeysPage';
import AccountSettingsPage from './pages/console/AccountSettingsPage';
import BillingPage from './pages/console/BillingPage';
import MockCheckoutPage from './pages/console/MockCheckoutPage';
import MockPortalPage from './pages/console/MockPortalPage';
import OrganizationSettingsPage from './pages/console/OrganizationSettingsPage';
import ArtifactsPage from './pages/console/ArtifactsPage';
import WorkersPage from './pages/console/WorkersPage';
import UsagePage from './pages/console/UsagePage';
import PlatformOverviewPage from './pages/platform/PlatformOverviewPage';
import PlatformTenantsPage from './pages/platform/PlatformTenantsPage';
import PlatformPartnerDetailPage from './pages/platform/PlatformPartnerDetailPage';
import PlatformPartnerOnboardingPage from './pages/platform/PlatformPartnerOnboardingPage';
import PlatformUsersPage from './pages/platform/PlatformUsersPage';
import PlatformUserDetailPage from './pages/platform/PlatformUserDetailPage';
import PlatformJobsPage from './pages/platform/PlatformJobsPage';
import PlatformWorkersPage from './pages/platform/PlatformWorkersPage';
import PlatformPoliciesPage from './pages/platform/PlatformPoliciesPage';
import PlatformAuditPage from './pages/platform/PlatformAuditPage';
import PlatformSettingsPage from './pages/platform/PlatformSettingsPage';
import PlatformTeamPage from './pages/platform/PlatformTeamPage';
import PartnerAuditPage from './pages/console/PartnerAuditPage';
import {
  TENANT_VIEW,
  PROJECT_VIEW,
  JOB_VIEW,
  JOB_CREATE,
  RIGGING_JOB_VIEW,
  ARTIFACT_VIEW,
  WORKER_VIEW,
  MEMBER_VIEW,
  API_KEY_VIEW,
  USAGE_VIEW,
  AUDIT_VIEW,
  BILLING_VIEW,
  TENANT_MANAGE,
  AI_TECHNICAL_ARTIST_USE,
  PLATFORM_ACCESS,
  PLATFORM_TENANT_VIEW,
  PLATFORM_TENANT_MANAGE,
  PLATFORM_USER_VIEW,
  PLATFORM_JOB_VIEW,
  PLATFORM_WORKER_VIEW,
  PLATFORM_POLICY_VIEW,
  PLATFORM_AUDIT_VIEW,
  PLATFORM_SETTINGS_VIEW,
} from './auth/permissions';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './ProtectedRoute';

const StudioPage = lazy(() => import('./studio/StudioPage'));
const CharacterStagePage = lazy(() => import('./character-stage/CharacterStagePage'));

function RedirectJobDetail() {
  const { jobId } = useParams();
  return <Navigate to={`/console/jobs/${jobId}`} replace />;
}

function RedirectProjectDetail() {
  const { projectId } = useParams();
  return <Navigate to={`/console/projects/${projectId}`} replace />;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/studio" element={<Suspense fallback={<div role="status">Loading Studio…</div>}><StudioPage /></Suspense>} />
          <Route path="/character-stage" element={<Suspense fallback={<div role="status" style={{ padding: 40 }}>Loading Character Stage…</div>}><CharacterStagePage /></Suspense>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invitations/accept" element={<AcceptInvitePage />} />
          <Route element={<ProtectedRoute />}>
            {/* Legacy product routes → console */}
            <Route path="/jobs" element={<Navigate to="/console/jobs" replace />} />
            <Route path="/jobs/new" element={<Navigate to="/console/jobs/new" replace />} />
            <Route path="/jobs/:jobId" element={<RedirectJobDetail />} />
            <Route path="/projects" element={<Navigate to="/console/projects" replace />} />
            <Route path="/projects/:projectId" element={<RedirectProjectDetail />} />
            <Route path="/app" element={<Navigate to="/console" replace />} />
            <Route path="/app/*" element={<Navigate to="/console" replace />} />

            {/* Customer / developer console */}
            <Route path="/console" element={<ConsoleLayout />}>
              <Route
                index
                element={
                  <RequireTenantPermission permission={TENANT_VIEW}>
                    <OverviewPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="projects"
                element={
                  <RequireTenantPermission permission={PROJECT_VIEW}>
                    <ProjectsListPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="projects/:projectId"
                element={
                  <RequireTenantPermission permission={PROJECT_VIEW}>
                    <ProjectDetailPage />
                  </RequireTenantPermission>
                }
              />
              <Route path="projects/:projectId/files" element={<RequireTenantPermission permission={PROJECT_VIEW}><ProjectFilesPage /></RequireTenantPermission>} />
              <Route path="projects/:projectId/settings" element={<RequireTenantPermission permission={PROJECT_VIEW}><ProjectSettingsPage /></RequireTenantPermission>} />
              <Route path="projects/:projectId/departments/:department" element={<RequireTenantPermission permission={PROJECT_VIEW}><ProductionDepartmentPage /></RequireTenantPermission>} />
              <Route path="projects/:projectId/departments/:department/:recordId" element={<RequireTenantPermission permission={PROJECT_VIEW}><ProductionWorkflowPage /></RequireTenantPermission>} />
              <Route path="projects/:projectId/departments/:department/:recordId/studio" element={<RequireTenantPermission permission={PROJECT_VIEW}><Suspense fallback={<div role="status">Loading Studio…</div>}><StudioPage /></Suspense></RequireTenantPermission>} />
              <Route
                path="projects/:projectId/rigging/*"
                element={
                  <RequireTenantPermission permission={PROJECT_VIEW}>
                    <ProjectRiggingPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="ai"
                element={
                  <RequireTenantPermission permission={AI_TECHNICAL_ARTIST_USE}>
                    <TechnicalArtistPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="jobs"
                element={
                  <RequireTenantPermission permission={JOB_VIEW}>
                    <JobsPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="jobs/new"
                element={
                  <RequireTenantPermission permission={JOB_CREATE}>
                    <NewBakePage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="jobs/:jobId"
                element={
                  <RequireTenantPermission permission={JOB_VIEW}>
                    <JobDetailPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="rigging-jobs"
                element={
                  <RequireTenantPermission permission={RIGGING_JOB_VIEW}>
                    <PartnerRiggingJobsPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="artifacts"
                element={
                  <RequireTenantPermission permission={ARTIFACT_VIEW}>
                    <ArtifactsPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="workers"
                element={
                  <RequireTenantPermission permission={WORKER_VIEW}>
                    <WorkersPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="usage"
                element={
                  <RequireTenantPermission permission={USAGE_VIEW}>
                    <UsagePage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="activity"
                element={
                  <RequireTenantPermission permission={AUDIT_VIEW}>
                    <PartnerAuditPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="team"
                element={
                  <RequireTenantPermission permission={MEMBER_VIEW}>
                    <TeamPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="team/roles"
                element={
                  <RequireTenantPermission permission={MEMBER_VIEW}>
                    <TeamRolesPage />
                  </RequireTenantPermission>
                }
              />
              <Route path="tenants/new" element={<Navigate to="/console/partners/new" replace />} />
              <Route path="partners/new" element={<CreateTenantPage />} />
              <Route
                path="api-keys"
                element={
                  <RequireTenantPermission permission={API_KEY_VIEW}>
                    <ApiKeysPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="organization"
                element={<Navigate to="/console/partner" replace />}
              />
              <Route
                path="partner"
                element={
                  <RequireTenantPermission permission={TENANT_MANAGE}>
                    <OrganizationSettingsPage />
                  </RequireTenantPermission>
                }
              />
              <Route path="account" element={<AccountSettingsPage />} />
              <Route
                path="account/billing"
                element={
                  <RequireTenantPermission permission={BILLING_VIEW}>
                    <BillingPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="account/subscription"
                element={<Navigate to="/console/account/billing?tab=plan" replace />}
              />
              <Route
                path="account/payment-methods"
                element={<Navigate to="/console/account/billing?tab=payment" replace />}
              />
              <Route
                path="account/invoices"
                element={<Navigate to="/console/account/billing?tab=invoices" replace />}
              />
              <Route
                path="account/billing/mock-checkout"
                element={
                  <RequireTenantPermission permission={BILLING_VIEW}>
                    <MockCheckoutPage />
                  </RequireTenantPermission>
                }
              />
              <Route
                path="account/billing/portal"
                element={
                  <RequireTenantPermission permission={BILLING_VIEW}>
                    <MockPortalPage />
                  </RequireTenantPermission>
                }
              />
            </Route>

            {/* Platform console */}
            <Route
              path="/platform"
              element={
                <RequirePlatformPermission permission={PLATFORM_ACCESS}>
                  <PlatformLayout />
                </RequirePlatformPermission>
              }
            >
              <Route index element={<Navigate to="/platform/overview" replace />} />
              <Route path="overview" element={<PlatformOverviewPage />} />
              <Route
                path="tenants"
                element={<Navigate to="/platform/partners" replace />}
              />
              <Route
                path="partners"
                element={
                  <RequirePlatformPermission permission={PLATFORM_TENANT_VIEW}>
                    <PlatformTenantsPage />
                  </RequirePlatformPermission>
                }
              />
              <Route
                path="partners/new"
                element={
                  <RequirePlatformPermission permission={PLATFORM_TENANT_MANAGE}>
                    <PlatformPartnerOnboardingPage />
                  </RequirePlatformPermission>
                }
              />
              <Route
                path="partners/:partnerId"
                element={
                  <RequirePlatformPermission permission={PLATFORM_TENANT_VIEW}>
                    <PlatformPartnerDetailPage />
                  </RequirePlatformPermission>
                }
              />
              <Route
                path="users"
                element={
                  <RequirePlatformPermission permission={PLATFORM_USER_VIEW}>
                    <PlatformUsersPage />
                  </RequirePlatformPermission>
                }
              />
              <Route
                path="users/:userId"
                element={
                  <RequirePlatformPermission permission={PLATFORM_USER_VIEW}>
                    <PlatformUserDetailPage />
                  </RequirePlatformPermission>
                }
              />
              <Route
                path="team"
                element={
                  <RequirePlatformPermission permission={PLATFORM_USER_VIEW}>
                    <PlatformTeamPage />
                  </RequirePlatformPermission>
                }
              />
              <Route
                path="jobs"
                element={
                  <RequirePlatformPermission permission={PLATFORM_JOB_VIEW}>
                    <PlatformJobsPage />
                  </RequirePlatformPermission>
                }
              />
              <Route
                path="workers"
                element={
                  <RequirePlatformPermission permission={PLATFORM_WORKER_VIEW}>
                    <PlatformWorkersPage />
                  </RequirePlatformPermission>
                }
              />
              <Route
                path="policies"
                element={
                  <RequirePlatformPermission permission={PLATFORM_POLICY_VIEW}>
                    <PlatformPoliciesPage />
                  </RequirePlatformPermission>
                }
              />
              <Route
                path="audit"
                element={
                  <RequirePlatformPermission permission={PLATFORM_AUDIT_VIEW}>
                    <PlatformAuditPage />
                  </RequirePlatformPermission>
                }
              />
              <Route
                path="settings"
                element={
                  <RequirePlatformPermission permission={PLATFORM_SETTINGS_VIEW}>
                    <PlatformSettingsPage />
                  </RequirePlatformPermission>
                }
              />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
